import { BehaviorSubject } from 'rxjs';
import moment from 'moment';
import ResponseHandlerService from './ResponseHandlerService';
import CoreApi from '../api/core/index';
import { sortBy, prop } from 'ramda';
import { SUBSCRIPTION_TYPES } from 'constants/general';
import { isTeacher } from 'lib/helpers';

export interface ISubscription {
  details?: {},
  total_enrollments: number,
  enrollments_used: number,
  remaining_enrollments: number,
  outstanding_debt?: {}[],
  subscriptionType: string,
  billingPeriodEnd: '',
}

class SubscriptionService {
  static instance: SubscriptionService;
  private responseHandler = ResponseHandlerService.getInstance();
  private availablePlans$ = new BehaviorSubject([]);
  private subscription$: BehaviorSubject<any>;
  public activePlan$ = new BehaviorSubject({});

  constructor() {
    this.subscription$ = new BehaviorSubject<any>({
      total_enrollments: 0,
      enrollments_used: 0,
      remaining_enrollments: 0,
      subscriptionType: SUBSCRIPTION_TYPES.DEMO,
      billingPeriodEnd: '',
    });
  }

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) SubscriptionService.instance = new SubscriptionService();
    return SubscriptionService.instance;
  }

  /* Setters */

  setActivePlan = (id: number) => {
    const activePlan = this.getAvailablePlans().getValue().filter((plan: any) => plan.id === id)[0];
    return this.activePlan$.next(activePlan);
  };

  setSubscription$ = (subscription: any) => this.subscription$.next(subscription);

  /* Getters */

  getActivePlan = () => this.activePlan$;
  getAvailablePlans = () => this.availablePlans$;
  getSubscription = (): any => this.subscription$;

  updateSubscriptionEnrollmentsUsed = (enronllmentsUsed: number) => {
    const currentSubscription = this.subscription$.getValue();
    const { total_enrollments, enrollments_used } = currentSubscription;
    const newEnrollmentsUsed = enrollments_used + enronllmentsUsed;
    return this.setSubscription$({
      ...currentSubscription,
      ...{
        enrollments_used: newEnrollmentsUsed,
        remaining_enrollments: Math.max(0, total_enrollments - newEnrollmentsUsed) || 0,
      }
    });
  }

  /* API Endpoints */

  /**
   * Get the organization's active subscription.
   * The API returns n number of subscriptions for the current organization,
   * but the UI currently only supports 1 subscription plan per account.
   * Hence, we filter the subscriptions array to return only 1 result.
   *
   */
  fetchSubscription = () => this.responseHandler.handleResponse({
    promise: CoreApi.organizationScope.getSubscriptions(),
    successCallback: (data: any) => {
      const activeSubscription = isTeacher() ? data.subscription : data.subscriptions
        .filter((subscription: any) => subscription.id === data.active_subscription_id)[0] || {};
      this.setActivePlan(activeSubscription?.premium_plan_id);
      const totalEnrollments = data.subscription_enrollments || 0;
      const usedEnrollments = data.current_subscription_usage || 0;

      return this.setSubscription$({
        details: activeSubscription,
        total_enrollments: totalEnrollments,
        enrollments_used: usedEnrollments,
        remaining_enrollments: Math.max(0, totalEnrollments - usedEnrollments) || 0,
        subscriptionType: data?.subscription_type,
        billingPeriodEnd: moment(activeSubscription?.current_period_end).format('DD MMM YYYY'),
      })
    }
  });

  getOutstandingDebt = (id: number) => this.responseHandler.handleResponse({
    promise: CoreApi.organizationScope.getOutstandingDebt(id),
    successCallback: (data: any) => {
      const subscriptionData = this.subscription$.getValue()
      subscriptionData.outstanding_debt = data

      return this.setSubscription$(subscriptionData)
    }
  })

  cancelSubscription = (id: number) => CoreApi.organizationScope.cancelSubscription(id)
    .then(() => this.fetchSubscription());

  getPendingAmountDue = (id: number) => CoreApi.organizationScope.getPendingAmountDue(id)
    .then((response: any) => {
      return response;
    }) ;

  createSubscription = (body: any) => this.responseHandler.handleResponse({
    promise: CoreApi.organizationScope.createSubscription(body),
    successCallback: (data: any) => {
      this.setActivePlan(data.subscription && data.subscription.premium_plan_id);
      const totalEnrollments = data.subscription_enrollments || 0;
      const usedEnrollments = data.current_subscription_usage || 0;

      return this.setSubscription$({
        details: data.subscription,
        total_enrollments: totalEnrollments,
        enrollments_used: usedEnrollments,
        remaining_enrollments: Math.max(0, totalEnrollments - usedEnrollments) || 0,
        type: data?.subscription_type,
        subscriptionType: data?.subscription_type,
        billingPeriodEnd: moment(data?.subscription?.current_period_end).format('DD MMM YYYY'),
      })
    }
  });

  updateSubscription = (subcription_id: any, body: any) => this.responseHandler.handleResponse({
    promise: CoreApi.organizationScope.updateSubscription(subcription_id, body),
    successCallback: (data: any) => {
      this.setActivePlan(data.subscription && data.subscription.premium_plan_id);
      const totalEnrollments = data.subscription_enrollments || 0;
      const usedEnrollments = data.current_subscription_usage || 0;

      return this.setSubscription$({
        details: data.subscription,
        total_enrollments: totalEnrollments,
        enrollments_used: usedEnrollments,
        remaining_enrollments: Math.max(0, totalEnrollments - usedEnrollments) || 0,
        type: data?.subscription_type,
        subscriptionType: data?.subscription_type,
        billingPeriodEnd: moment(data?.subscription?.current_period_end).format('DD MMM YYYY'),
      })
    }
  });

  getPremiumPlans = () => this.responseHandler.handleResponse({
    promise: CoreApi.organizationScope.getPremiumPlans(),
    successCallback: (plans: any) => {
      const plansSorted = sortBy(prop('priority'), plans);

      return this.availablePlans$.next(plansSorted)
    },
  });

  getPreviewUpdateSubscription = (id: number, date: string, quantity: number) => CoreApi.organizationScope.getPreviewUpdateSubscription(id, date, quantity)
    .then((response: any) => {
      return response;
    }) ;
}

export default SubscriptionService;
